/**
 * @file The frontend twin of healthcare's `FormStatus::isDone()`.
 * @module utils/forms/formStatus
 *
 * ═══ WHY THIS EXISTS (ClickUp 86d3y4ebp) ══════════════════════════════════════════════
 *
 * `assigned_forms.status` has TWO done states — `Completed` and `Reviewed`. `Reviewed` is
 * `Completed` plus a practitioner having read it, so a form that reaches it has strictly
 * MORE done to it, not less. Any test written as `status === "Completed"` therefore reads a
 * finished form as unfinished, and the symptom is always a disappearance rather than an
 * error: the row stops being clickable, the response stops reaching the report, the count
 * comes up short. Nothing throws and nothing logs.
 *
 * The predicate was restated inline at nine call sites. Six had both states; three had only
 * `Completed` and were the bugs this module was created to fix. Two more of exactly this
 * family were fixed inside ClickUp 86d3t4ev9 — `InlineStageRenderer` (a reviewed
 * practitioner form read as reusable, so a treatment-plan stage re-opened a finished
 * assignment) and healthcare's `FormAISuggestionService` (which tested lowercase
 * `'completed'` and so matched NOTHING, leaving every AI form suggestion ungrounded).
 *
 * That is five bugs from one two-line predicate. It gets one home.
 *
 * ⚠️ THE BACKEND IS THE TWIN, NOT THE MIRROR OF SOMETHING HERE.
 * `App\Enums\FormStatus::isDone()` is the authority, and it derives its `done()` /
 * `outstanding()` query scopes from itself precisely so the object-side and query-side
 * definitions cannot drift. This is the same idea across the wire. Change one and change
 * the other in the same release.
 *
 * ═══ WHY IT IS CASE-INSENSITIVE WHEN THE BACKEND ENUM IS NOT ══════════════════════════
 *
 * `FormStatus` is a backed enum, so PHP can only ever hold one of five exact strings and a
 * case-sensitive `===` there is total. This side receives JSON, where the same guarantee
 * does not hold: the column was a bare `string` until ClickUp 86d3t4ev9 / F6, lowercase
 * `'pending'` and `'completed'` were written by two treatment-plan classes for a year, and
 * the `2026_08_05_000004_normalise_assigned_form_status` migration REPORTS unrecognised
 * legacy values rather than coercing them.
 *
 * Folding case is what lets ONE predicate replace both shapes that existed here — the six
 * title-case comparisons and `InlineStageRenderer`'s deliberately lowercased one — without
 * changing what any of them decided. Doing it the other way would have made that call site
 * stricter as a side effect of a refactor, on a treatment-plan path, silently.
 *
 * ⚠️ THIS IS NOT A LICENCE TO WRITE A LOWERCASE STATUS. `Rule::enum()` rejects one on every
 * writer; this is tolerance on the READ side for rows that already exist.
 *
 * ═══ THERE IS DELIBERATELY NO `isFormOutstanding()` ═══════════════════════════════════
 *
 * The backend has one, and it is NOT `!isDone()`: `Cancelled` is neither done nor
 * outstanding, because nobody is waiting on a cancelled assignment. Nothing here needs that
 * distinction yet — `FormsTab`'s "pending" lists are `!isFormDone(...)` and so count
 * cancelled assignments as pending, which is a real (separate, pre-existing) discrepancy
 * with the backend and is tracked on its own ticket. Adding a third state to this module
 * without a call site would invite someone to swap it in and change that behaviour by
 * accident.
 */

/**
 * The two states that mean "this assignment is finished", lowercased for comparison.
 *
 * Exported for tests and for anything that needs the set itself (a `whereIn`-shaped filter,
 * say) rather than the predicate — the same reason healthcare exposes `FormStatus::done()`
 * alongside `isDone()`. Derive from it; do not restate it.
 */
export const DONE_FORM_STATUSES: readonly string[] = ["completed", "reviewed"];

/**
 * Is this assignment finished, from the practitioner's point of view?
 *
 * Accepts the raw `assigned_forms.status` string. Null/undefined/empty is `false` — an
 * assignment whose status we do not know is not one we may treat as submitted.
 */
export const isFormDone = (status?: string | null): boolean =>
	DONE_FORM_STATUSES.includes((status ?? "").trim().toLowerCase());
